import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { Renderers } from "../render/prose";

/** `prose`, in the dsh register — the agent's reply, unlabeled, as markdown.
 *
 * The MECHANICS here are the native skin's, deliberately and wholesale:
 * `std-prosemirror` bound read-only through the shared GFM serde, an async
 * bind so the lazy module import stays out of the render frame, and one
 * stylesheet pushed into the editor's shadow root. Two markdown parsers in
 * one codebase is two sets of bugs, and a second skin is not a reason to
 * grow one — a skin is a stylesheet with a name, and everything below the
 * stylesheet should be the same code.
 *
 * `addStyle` rather than this component's own `$styles` because the
 * document lives in the EDITOR's shadow root; a bare `p { … }` rule
 * declared here would match nothing at all. Only inherited properties and
 * custom properties cross that boundary. It is keyed and idempotent, so a
 * recycled row re-adopts the same sheets instead of stacking copies.
 *
 * What IS this skin's is the rhythm. dsh sets the reply at the paragraph
 * size against 14px rows — one step of the ramp above the disclosure rows
 * it sits among — and separates blocks on a 16px beat rather than the
 * native skin's 20. Both numbers answer the same constraint: the seam
 * between two paragraphs has to beat the line advance INSIDE one, or the
 * pair reads as a single block set slightly tight. At this size and a 1.75
 * ratio consecutive baselines sit about 14.3px apart measured cap to
 * baseline, so 16px clears it — tighter than native, which is the register
 * difference, and not so tight that it stops being a break.
 *
 * `Markdown.DEFAULT` is NOT the GFM serde. It is plain prosemirror-markdown,
 * and a table or a strikethrough under it survives as literal pipes and
 * tildes. */
@component({ name: "std-dsh-turn-prose" })
export class DshTurnProse extends LonaWebComponent {
  private $editor: HTMLElement = this.$("editor");

  static makeWith(props: DshTurnProse.Props): DshTurnProse {
    return (DshTurnProse.make() as DshTurnProse).bind(props);
  }

  /** Idempotent: both `addStyle` calls are keyed, and the editor bind
   * REPLACES the document rather than appending to it, so a pooled row
   * arriving from another turn renders only the text it was just given. */
  bind(props: DshTurnProse.Props): this {
    // Through the prose seam (render/prose.ts): the page's renderer paints
    // into #editor, and this skin's sheet rides along as `opts.style` so a
    // shadow-rooted renderer adopts it where the document actually lives.
    // Fire-and-forget: the transcript measures row height with a
    // ResizeObserver rather than declaring it.
    void Renderers.prose().render(this.$editor, props.text, {
      style: DshTurnProse.$document,
      key: "std-dsh-turn-prose",
    });
    return this;
  }

  /** The editor destroys its own view on recycle — this component holds no
   * per-row state of its own to reset, which is the whole reason the
   * mechanics were copied rather than reinvented. */
  onRecycle(): void {
    super.onRecycle();
  }

  /** Adopted INTO the editor's shadow root. Selectors are scoped under
   * .prose so they outrank the editor's own bare li/ol/pre defaults
   * regardless of which sheet is appended last. */
  static $document = css`
    #editor {
      /* The editor is chrome-less here: a transcript row is not a scroll
         container and has no caret. */
      overflow: visible !important;
      height: auto !important;
      min-height: 0 !important;
      cursor: auto;
      /* Load-bearing and not obviously so. std-prosemirror declares
         :host { display: contents; width: 200px } and then
         #editor { width: inherit }. Under display: contents the host draws
         no box, so the 200px never LOOKS like a width — but it is still the
         host's computed value, and inherit copies it straight onto the
         editor. */
      width: 100% !important;
    }
    .prose, .ProseMirror {
      padding: 0;
      color: var(--text-pri, #1f2328);
      white-space: pre-wrap;
      overflow-wrap: anywhere;
    }
    .prose > :first-child {
      margin-top: 0;
    }
    .prose > :last-child {
      margin-bottom: 0;
    }
    /* The reply's body. --font-size goes on the paragraphs themselves
       rather than on the editor: p is a ramp selector and sets --font-size
       to --p-size on itself, so a value inherited from the container would
       be overwritten a level down.

       16px of seam, which is this skin's whole spacing decision — see the
       note above the class. It has to move if the ratio does. */
    .prose p {
      --font-size: var(--p-size);
      --line-height-ratio: 1.75;
      --margin-top: 0px;
      --margin-bottom: 16px;
    }
    .prose h1,
    .prose h2,
    .prose h3,
    .prose h4,
    .prose h5,
    .prose h6 {
      margin: 16px 0 6px;
      font-weight: 600;
      line-height: 1.35;
    }
    /* DERIVED from the body rather than picked: a reply's headings are the
       one thing that legitimately outranks the reading size, and two steps
       is all a chat reply has ever needed. h3 and down are the body size and
       say "heading" with weight and colour. */
    .prose h1 {
      --font-size: var(--h5-size);
    }
    .prose h2 {
      --font-size: var(--h6-size);
    }
    .prose h3,
    .prose h4,
    .prose h5,
    .prose h6 {
      --font-size: var(--p-size);
      color: var(--text-sec);
    }
    /* Lists and quotes ride the same 16px beat as the paragraphs, so a
       reply that alternates between them keeps one rhythm rather than two. */
    .prose ul,
    .prose ol {
      margin: 0 0 16px;
      padding-left: 22px;
    }
    .prose li {
      margin: 2px 0;
    }
    .prose li > p {
      --margin-bottom: 0px;
      margin: 0;
    }
    .prose code {
      font-family: var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace);
      --font-size: var(--turn-fine-size, var(--text-8px));
      padding: 1px 4px;
      border-radius: 4px;
      background: var(--surface-sunken, rgba(127, 127, 127, 0.12));
    }
    /* Code must not be reflowed to fit; it scrolls inside its own box so
       the reading column stays put. */
    .prose pre {
      margin: 0 0 16px;
      padding: 12px 16px;
      border: 1px solid var(--divider, rgba(127, 127, 127, 0.25));
      border-radius: 12px;
      background: var(--bg-hover, rgba(127, 127, 127, 0.08));
      overflow-x: auto;
      white-space: pre;
    }
    .prose pre code {
      padding: 0;
      background: none;
      --font-size: var(--turn-fine-size, var(--text-8px));
      --line-height-ratio: 1.5;
    }
    .prose blockquote {
      margin: 0 0 16px;
      padding-left: 12px;
      border-left: 2px solid var(--divider, rgba(127, 127, 127, 0.35));
      color: var(--text-sec, #59616e);
    }
    /* Tables are NOT styled here — MARKDOWN_TABLE_STYLE is adopted
       alongside this sheet, so there is one markdown table look in the
       codebase rather than one per skin. */
    .prose table {
      margin-top: 4px;
      margin-bottom: 16px;
      white-space: normal;
    }
    .prose a {
      color: var(--accent, #4a7dff);
      text-decoration: underline;
      text-underline-offset: 2px;
    }
  `;

  static $styles = [
    DshTurnProse.$document,
    css`
      :host {
        display: block;
        min-width: 0;
        width: 100%;
      }
      /* Crosses into the editor by inheritance: its ProseMirror block reads
         padding from this custom property. */
      #editor {
        --editor-padding: 0;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <div id="root"><std-prosemirror id="editor"></std-prosemirror></div>
  `;
}

export namespace DshTurnProse {
  export type Props = {
    /** The coalesced message body, as the agent wrote it — markdown. */
    text: string;
  };
}
